export interface RankingRowInput {
  vehicle_class: string | null;
  fuel_type: string | null;
  atv_type: string | null;
  mpg_combined: number | null;
  mpg_city: number | null;
  mpg_hwy: number | null;
  annual_fuel_cost: number | null;
  co2_tailpipe_gpm: number | null;
  year: number;
}

export interface RankingDef {
  slug: string;
  h1: string;
  title: string;
  description: string;
  /** Column shown in the ranked table. */
  metricLabel: string;
  unit: string;
  /** Higher is better? */
  mode: "max" | "min";
  metric: (r: RankingRowInput) => number | null;
  filter: (r: RankingRowInput) => boolean;
  intro: string;
  /** Extra context paragraph under the table. */
  notes: string;
}

const cls = (r: RankingRowInput) => (r.vehicle_class ?? "").toLowerCase();
const fuel = (r: RankingRowInput) => `${r.fuel_type ?? ""} ${r.atv_type ?? ""}`.toLowerCase();
const recent = (r: RankingRowInput) => r.year >= 2020;

const mpg = (r: RankingRowInput) => r.mpg_combined;

function mpgRanking(
  slug: string,
  noun: string,
  h1: string,
  filter: (r: RankingRowInput) => boolean,
  notes: string,
): RankingDef {
  return {
    slug,
    h1,
    title: `${h1} (EPA Combined MPG)`,
    description: `The ${noun} with the highest EPA combined fuel economy, ranked from official test data with city and highway figures and estimated annual fuel cost.`,
    metricLabel: "Combined MPG",
    unit: "mpg",
    mode: "max",
    metric: mpg,
    filter: (r) => recent(r) && !!r.mpg_combined && filter(r),
    intro: `These are the ${noun} with the best EPA combined fuel economy on record. Every figure is an official EPA test result, not a manufacturer estimate, and each row links to that model's full year-by-year data.`,
    notes,
  };
}

export const RANKINGS: RankingDef[] = [
  mpgRanking(
    "best-gas-mileage-suvs",
    "SUVs",
    "Best Gas Mileage SUVs",
    (r) => cls(r).includes("sport utility") || cls(r).includes("suv"),
    "Hybrid and plug-in SUVs dominate the top of this list. If you want the best economy from a conventional gas SUV, look for front-wheel drive and the smallest available engine — all-wheel drive typically costs 1 to 3 mpg combined.",
  ),
  mpgRanking(
    "best-gas-mileage-trucks",
    "pickup trucks",
    "Best Gas Mileage Trucks",
    (r) => cls(r).includes("pickup") || cls(r).includes("truck"),
    "Fuel economy in pickups tracks closely with cab and bed configuration, drivetrain, and engine. A two-wheel-drive model with the base engine is almost always the efficiency leader within a given truck line.",
  ),
  mpgRanking(
    "best-gas-mileage-cars",
    "cars",
    "Best Gas Mileage Cars",
    (r) =>
      cls(r).includes("car") &&
      !cls(r).includes("sport utility") &&
      !cls(r).includes("pickup"),
    "Compact hybrids lead this category outright. Among non-hybrids, small-displacement turbocharged engines with a CVT or 8-plus-speed automatic post the strongest combined numbers.",
  ),
  mpgRanking(
    "best-gas-mileage-minivans",
    "minivans and passenger vans",
    "Best Gas Mileage Minivans & Vans",
    (r) => cls(r).includes("van"),
    "The hybrid minivan is the standout in this class. Among conventional vans, cylinder deactivation and higher gear counts account for most of the spread.",
  ),
  mpgRanking(
    "most-fuel-efficient-hybrids",
    "hybrids",
    "Most Fuel-Efficient Hybrid Vehicles",
    (r) => fuel(r).includes("hybrid"),
    "Hybrid economy is strongest in city driving, where the electric motor covers low-speed work and regenerative braking recovers energy. That's the reverse of a conventional car, which usually does best on the highway.",
  ),
  {
    slug: "most-efficient-electric-cars",
    h1: "Most Efficient Electric Cars",
    title: "Most Efficient Electric Cars (MPGe)",
    description:
      "Electric vehicles ranked by EPA combined efficiency in MPGe, with estimated annual energy cost — the number that decides what you actually pay to drive.",
    metricLabel: "Combined MPGe",
    unit: "MPGe",
    mode: "max",
    metric: mpg,
    filter: (r) => recent(r) && !!r.mpg_combined && fuel(r).includes("electric"),
    intro:
      "MPGe converts electricity use into a gasoline-equivalent figure, so a higher number means less energy consumed per mile. It measures efficiency, not range — a large, efficient EV can travel farther than a small one with a better MPGe rating.",
    notes:
      "Efficiency falls in cold weather and at sustained highway speed. Real-world consumption also depends on charging losses, which the EPA figure already accounts for at the wall.",
  },
  {
    slug: "cheapest-cars-to-own-fuel",
    h1: "Cheapest Cars to Fuel",
    title: "Cheapest Cars to Fuel (Annual Fuel Cost)",
    description:
      "Vehicles ranked by the lowest estimated annual fuel cost from the EPA, based on 15,000 miles a year — the clearest measure of what a car costs to drive.",
    metricLabel: "Annual fuel cost",
    unit: "$/yr",
    mode: "min",
    metric: (r) => r.annual_fuel_cost,
    filter: (r) => recent(r) && !!r.annual_fuel_cost,
    intro:
      "Annual fuel cost bundles fuel economy and fuel type into one number. An electric vehicle with modest efficiency can still beat a high-MPG hybrid here, because electricity per mile usually costs less than gasoline.",
    notes:
      "The EPA calculates this at 15,000 miles a year using national average fuel and electricity prices. Your figure scales directly with your annual mileage and local prices.",
  },
  {
    slug: "lowest-emissions-vehicles",
    h1: "Lowest Emission Vehicles",
    title: "Lowest Emission Vehicles (CO2 Grams per Mile)",
    description:
      "Vehicles ranked by the lowest tailpipe CO2 output in grams per mile, from EPA test data, with combined MPG and annual fuel cost alongside.",
    metricLabel: "Tailpipe CO2",
    unit: "g/mi",
    mode: "min",
    metric: (r) => r.co2_tailpipe_gpm,
    filter: (r) => recent(r) && r.co2_tailpipe_gpm != null,
    intro:
      "Tailpipe CO2 tracks fuel burned almost exactly, so this ranking mirrors fuel economy — with one exception: battery-electric vehicles emit nothing at the tailpipe and sit at zero.",
    notes:
      "Tailpipe output excludes emissions from producing the fuel or electricity. For an EV, the upstream figure depends on how your regional grid generates power.",
  },
  {
    slug: "best-highway-mpg-vehicles",
    h1: "Best Highway MPG Vehicles",
    title: "Best Highway MPG Vehicles",
    description:
      "Vehicles with the highest EPA highway fuel economy — the ranking that matters if most of your miles are interstate commuting or road trips.",
    metricLabel: "Highway MPG",
    unit: "mpg",
    mode: "max",
    metric: (r) => r.mpg_hwy,
    filter: (r) => recent(r) && !!r.mpg_hwy,
    intro:
      "Highway economy rewards aerodynamics and tall gearing rather than hybrid assistance. If your driving is mostly steady-speed, this list predicts your real fuel bill better than the combined rating does.",
    notes:
      "The EPA highway cycle averages about 48 mph with gentle acceleration. Sustained speeds above 70 mph, roof racks, and headwinds all cut meaningfully into these figures.",
  },
  {
    slug: "best-city-mpg-vehicles",
    h1: "Best City MPG Vehicles",
    title: "Best City MPG Vehicles",
    description:
      "Vehicles with the highest EPA city fuel economy — the ranking to use for stop-and-go commuting, delivery routes, and heavy urban driving.",
    metricLabel: "City MPG",
    unit: "mpg",
    mode: "max",
    metric: (r) => r.mpg_city,
    filter: (r) => recent(r) && !!r.mpg_city,
    intro:
      "City economy is where hybrids and EVs pull furthest ahead, because they recover braking energy and shut the engine off at a standstill instead of idling fuel away.",
    notes:
      "The EPA city cycle includes frequent stops and low average speed. Short trips in cold weather are harder still, since the engine spends much of the drive warming up.",
  },
];

export const RANKING_SLUGS = RANKINGS.map((r) => r.slug);

export function getRanking(slug: string): RankingDef | undefined {
  return RANKINGS.find((r) => r.slug === slug);
}
